import { useState } from 'react';
import { useQueryClient } from 'react-query';
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from '@/ui/design-system/components';
import { WIDGET_KEY } from '@/utils/hooks/reports';
import {
    normalizeListConfig,
    withListView,
} from '@/ui/components/AiAssistant/visuals';
import WidgetEditForm from './WidgetEditForm';
import WidgetPreview from './WidgetPreview';
import useAddWidget from './useAddWidget';
import { isExecutableArgs } from './widgetCatalog';

// Clone a widget's args for the draft so editing never mutates the
// persisted config.
const cloneArgs = (args = {}) => ({
    ...args,
    ...(args.filters ? { filters: { ...args.filters } } : {}),
    ...(args.entity_ids ? { entity_ids: [...args.entity_ids] } : {}),
    ...(args.hashtags ? { hashtags: [...args.hashtags] } : {}),
    // Spread each term too: a shallow copy would leave the draft's objects
    // aliased to the saved widget's.
    ...(args.exclude ? { exclude: args.exclude.map((t) => ({ ...t })) } : {}),
});

// The widget edit modal. A print-preview split: the draft widget sits on a gray
// canvas (left) like a page on a desk, the config form rides a white side panel
// (right), and Cancel/Save anchor the panel's footer. Owns draftArgs and
// draftTitle; Save lifts both to onSave(args, meta), Cancel/close discards. A
// fresh mount per widget (keyed by id in ReportView) reseeds cleanly.
function WidgetEditModal({ widget, open, onOpenChange, onSave }) {
    const [draftArgs, setDraftArgs] = useState(() => cloneArgs(widget.args));
    const [draftTitle, setDraftTitle] = useState(() => widget.title ?? '');

    // Seed the entity display name AND type from the widget's already-fetched
    // result in the react-query cache (the grid ran this exact config on view).
    // The type matters when the config doesn't pin one: the form's name
    // resolver must never fetch under a guessed type. The pair's related name
    // rides on the result itself (there is no _entity equivalent).
    const queryClient = useQueryClient();
    const cached = queryClient.getQueryData([WIDGET_KEY, widget.args]);
    const initialEntityName = cached?._entity?.name;
    const initialEntityType = cached?._entity?.type;
    const initialRelatedEntityName = cached?.result?.related_entity_name;

    // The free-text box reuses the add-widget agent path; the harvested config
    // replaces the draft (which re-derives the type and refreshes the preview).
    const { addWidget, isAdding, error, emptyResult } = useAddWidget(
        (widgets) => {
            if (widgets?.[0]) setDraftArgs(cloneArgs(widgets[0].args));
        },
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl gap-0 overflow-hidden p-0">
                {/* min-w-0: DialogContent is display:grid, so this row is a grid
                    item (default min-width:auto). Without min-w-0 the preview's
                    content sizes the grid track wider than max-w-5xl, overflowing
                    the clipped dialog and pushing the config panel off-screen. */}
                <div className="flex h-[80vh] max-h-[780px] min-h-[460px] min-w-0">
                    {/* Preview canvas — the draft widget floating on gray.
                        min-w-0 lets this flex child shrink below its content so a
                        wide widget title scrolls within the canvas instead of
                        squeezing the config panel. */}
                    <div className="hidden min-w-0 flex-1 flex-col border-r border-border-med-em bg-background-low-em md:flex">
                        <div className="flex-1 overflow-auto p-8">
                            <div className="flex min-h-full items-center justify-center">
                                <div className="w-full max-w-[600px]">
                                    <WidgetPreview
                                        args={draftArgs}
                                        title={draftTitle.trim() || undefined}
                                        onToggleAggregate={(next) =>
                                            setDraftArgs((prev) =>
                                                withListView(
                                                    prev,
                                                    next
                                                        ? 'combined'
                                                        : 'time_series',
                                                ),
                                            )
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Config panel — white, with its own header + footer. */}
                    <div className="flex w-full flex-col bg-background md:w-[420px] md:shrink-0">
                        <div className="flex h-14 shrink-0 items-center border-b border-border-med-em px-5">
                            <DialogTitle className="text-base font-semibold leading-none">
                                Edit widget
                            </DialogTitle>
                            <DialogDescription className="sr-only">
                                Adjust the widget configuration. The preview
                                updates live.
                            </DialogDescription>
                        </div>
                        <div className="flex-1 overflow-y-auto px-5 py-4">
                            <WidgetEditForm
                                draftArgs={draftArgs}
                                setDraftArgs={setDraftArgs}
                                draftTitle={draftTitle}
                                setDraftTitle={setDraftTitle}
                                initialEntityName={initialEntityName}
                                initialEntityType={initialEntityType}
                                initialRelatedEntityName={
                                    initialRelatedEntityName
                                }
                                describe={{
                                    run: addWidget,
                                    isRunning: isAdding,
                                    error,
                                    emptyResult,
                                }}
                            />
                        </div>
                        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border-med-em px-5 py-3">
                            <Button
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                disabled={!isExecutableArgs(draftArgs)}
                                onClick={() => {
                                    const normalized = normalizeListConfig(
                                        draftArgs,
                                        { title: draftTitle },
                                    );
                                    onSave(normalized.args, normalized.meta);
                                }}
                            >
                                Save changes
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default WidgetEditModal;
